import { BaseTenantCrudService } from '@/services/_base/BaseTenantCrudService'
import type { IFirestoreService } from '@/platform/firebase/firestore'
import { useAuthStore } from '@/stores/authStore'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/firebase/config'
import type { Agent, AgentConfig } from '@/types/Agent'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class AgentService extends BaseTenantCrudService<Agent> {
  constructor(firestore: IFirestoreService, session: Session) {
    super('agents', firestore, session)
  }

  // Override create to call Cloud Function instead of direct Firestore
  // @ts-expect-error - Intentionally different signature (returns token, not id)
  async create(data: { agentId: string; name: string }): Promise<{ agentToken: string }> {
    const provisionAgent = httpsCallable(functions, 'provisionAgent')
    const result = await provisionAgent(data)
    return result.data as { agentToken: string }
  }

  // Override update to only allow certain fields
  async update(
    id: string,
    data: Partial<Pick<Agent, 'enabled'> & { meta: Partial<Agent['meta']> }>,
  ): Promise<void> {
    const updateData: any = {}

    if (data.enabled !== undefined) {
      updateData.enabled = data.enabled
    }

    if (data.meta) {
      updateData.meta = {
        ...data.meta,
        // Preserve existing meta fields if not provided
      }
    }

    await this.firestore.updateDoc(this.getCollectionPath(), id, updateData)
  }

  // Regenerate token for an agent
  async regenerateToken(id: string): Promise<{ agentToken: string }> {
    const provisionAgent = httpsCallable(functions, 'provisionAgent')
    const agent = await this.get(id)
    if (!agent) {
      throw new Error('Agent not found')
    }

    const result = await provisionAgent({
      agentId: id,
      name: agent.meta.name,
    })
    return result.data as { agentToken: string }
  }

  // Export agent configuration for download
  async exportConfig(id: string, agentToken: string): Promise<AgentConfig> {
    const agent = await this.get(id)
    if (!agent) {
      throw new Error('Agent not found')
    }

    const authStore = useAuthStore()

    // Get Firebase config from environment or config
    const firebaseConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    }

    const authBrokerUrl =
      import.meta.env.VITE_AUTH_BROKER_URL ||
      `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-scrapboss-dev.cloudfunctions.net'}/agentAuthBroker`

    return {
      firebase: firebaseConfig,
      tenantId: authStore.session.tenantId!,
      agentId: id,
      agentToken,
      authBrokerUrl,
      agent: {
        name: agent.meta.name,
        location: undefined, // Could be added to agent meta if needed
        roles: [], // Could be added to agent meta if needed
        capabilities: [], // Could be added to agent meta if needed
      },
      devices: {
        cameras: [],
        scales: [],
        scanners: [],
      },
    }
  }
}
